import { ControlPointOutlined, RemoveCircleOutline } from "@mui/icons-material";
import { Box, IconButton } from "@mui/material";
import { ForwardedRef, forwardRef, ReactElement, ReactNode, Ref, useImperativeHandle } from "react";
import {
	FieldValues,
	useFieldArray,
	UseFieldArraySwap,
	UseFieldArrayMove,
	UseFieldArrayRemove,
	FieldArrayMethodProps,
} from "react-hook-form";

import { FormControl } from "../FormControl/FormControl";
import { useFormContext } from "../hooks/useFormContext";

import { useFormFieldArrayStyles } from "./FormFieldArray.styles";

type Field<T extends FieldValues = FieldValues> = Record<"__id", string> & T;

export type FormFieldArrayMethods<T extends FieldValues = FieldValues> = Omit<
	RenderProps<T>,
	"index" | "field" | "getName" | "disabled"
>;

export interface RenderProps<T extends FieldValues> {
	index: number;
	field: Field<T>;
	fields: Array<Field<T>>;
	getName: (fieldName?: string) => string;
	disabled?: boolean;
	swap: UseFieldArraySwap;
	move: UseFieldArrayMove;
	prepend: (value: T, options?: FieldArrayMethodProps) => void;
	append: (value: T, options?: FieldArrayMethodProps) => void;
	remove: UseFieldArrayRemove;
	insert: (index: number, value: T, options?: FieldArrayMethodProps) => void;
	update: (index: number, value: T) => void;
	replace: (values: T[]) => void;
}

interface Props<T extends FieldValues> {
	name: string;
	containerClassName?: string;
	className?: string;
	defaultAppendValue: T;
	renderItem: (props: RenderProps<T>) => ReactNode;
	disabledActions?: boolean;
	hideActions?: boolean;
	disabled?: boolean;
	isHideErrorField?: boolean;
	isHideError?: boolean;
	noDataText?: string;
}

const FormFieldArrayComponent = <T extends FieldValues = FieldValues>(
	{
		name,
		renderItem,
		containerClassName,
		className,
		defaultAppendValue,
		disabledActions,
		hideActions,
		disabled,
		isHideErrorField,
		isHideError,
		noDataText,
	}: Props<T>,
	ref: ForwardedRef<FormFieldArrayMethods<T>>,
) => {
	const { classes, cx } = useFormFieldArrayStyles();
	const { control } = useFormContext();
	const { fields, ...methods } = useFieldArray({ control, name, keyName: "__id" });

	useImperativeHandle(ref, () => ({
		fields: fields as Array<Field<T>>,
		...methods,
	}));

	return (
		<FormControl
			name={name}
			className={cx(classes.container, containerClassName)}
			disabled={disabled}
			isHideErrorField={isHideErrorField}
			isHideError={isHideError}
		>
			{Boolean(noDataText && !fields.length) && noDataText}
			{(fields as Array<Field<T>>).map((field, index) => (
				<Box key={field.__id} className={cx(classes.item, className)}>
					{renderItem({
						index,
						disabled,
						getName: (fieldName?: string) => `${name}.${index}${fieldName ? `.${fieldName}` : ""}`,
						field,
						fields: fields as Array<Field<T>>,
						...methods,
					})}
					{!hideActions && (
						<IconButton
							size="small"
							className={classes.button}
							disabled={disabledActions || disabled}
							onClick={() => {
								if (!index) {
									methods.append(defaultAppendValue);
								} else {
									methods.remove(index);
								}
							}}
						>
							{!index ? <ControlPointOutlined /> : <RemoveCircleOutline />}
						</IconButton>
					)}
				</Box>
			))}
		</FormControl>
	);
};

export const FormFieldArray = forwardRef(FormFieldArrayComponent) as <
	T extends FieldValues = FieldValues,
>(
	props: Props<T> & { ref?: Ref<FormFieldArrayMethods<T>> },
) => ReactElement;
